/**
 * Theme components — KpiCard, SectionCard, GradientDialog, plus a
 * barrel re-export of the pure design tokens from `theme-tokens.ts`.
 *
 *   import {
 *     KpiCard, SectionCard, GradientDialog,
 *     DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
 *     GRADIENTS, TONES, gradientForScore,
 *   } from "@pureid/ui";
 */

import * as React from "react";
import { Card, CardContent } from "./card";
import { Dialog, DialogContent } from "./dialog";
import { FieldLabel } from "./field-label";
import { IconBadge } from "./icon-badge";
import { cn } from "../lib/utils";
import {
  GRADIENTS,
  TONES,
  ACCENT_BAR_CARD,
  ACCENT_BAR_DIALOG,
  type Tone,
} from "./theme-tokens";

// Re-export every token so consumers can keep a single import path.
export * from "./theme-tokens";

// ---------------------------------------------------------------------------
// KPI Card — accent-topped stat card
// ---------------------------------------------------------------------------

interface KpiCardProps {
  value: string | number;
  label: string;
  sublabel?: string;
  /** Tone drives the top accent gradient and the IconBadge color. Defaults to "info" (blue). */
  tone?: Tone;
  /** Lucide-style icon component. Rendered inside an IconBadge so every KPI card gets the same well. */
  icon?: React.ComponentType<{ className?: string }>;
  /** Extra classes on the big number (e.g. tone-colored text for scores). */
  valueClass?: string;
  /** Optional decorative trend visual (e.g. a <Sparkline/>) shown at the card's trailing edge. */
  chart?: React.ReactNode;
  /** Escape hatch: override the tone-derived gradient with a raw GRADIENTS string. */
  gradient?: string;
  /**
   * When true, renders the hover / cursor affordance expected for a
   * click-through card. The caller wraps the card in a TanStack Router
   * Link so the strict `to` type checking is preserved at the call site.
   *
   *   <Link to="/admins" className="block"><KpiCard interactive .../></Link>
   */
  interactive?: boolean;
}

export function KpiCard({
  value, label, sublabel, tone = "brand", icon: Icon, valueClass, gradient, interactive, chart,
}: KpiCardProps) {
  const barGradient = gradient ?? TONES[tone].gradient;
  return (
    <Card
      className={cn(
        "relative overflow-hidden",
        interactive &&
          "cursor-pointer transition-colors hover:border-border focus-within:ring-2 focus-within:ring-primary/30",
      )}
    >
      <div className={`absolute top-0 left-0 right-0 ${ACCENT_BAR_CARD} bg-gradient-to-r ${barGradient}`} />
      <CardContent className="pt-5 pb-5">
        <div className="flex items-center gap-3">
          {Icon && <IconBadge icon={Icon} tone={tone} />}
          <div className="min-w-0">
            <p className={cn("text-2xl font-bold font-mono tabular-nums leading-none", valueClass)}>{value}</p>
            <FieldLabel className="mt-1.5">{label}</FieldLabel>
            {sublabel && <p className="text-xs text-muted-foreground mt-1 truncate">{sublabel}</p>}
          </div>
          {chart && <div className="ml-auto shrink-0 self-center">{chart}</div>}
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Section Card — card with gradient top accent
// ---------------------------------------------------------------------------

interface SectionCardProps {
  /** Raw GRADIENTS string. Defaults to GRADIENTS.brand. Prefer `tone` for new code. */
  gradient?: string;
  /** Tone drives the gradient. Overrides `gradient` if both passed. */
  tone?: Tone;
  children: React.ReactNode;
  className?: string;
  /** Optional DOM id so a section can be an in-page anchor target (pair with `scroll-mt-*`). */
  id?: string;
}

export function SectionCard({ gradient, tone, children, className, id }: SectionCardProps) {
  const barGradient = tone ? TONES[tone].gradient : (gradient ?? GRADIENTS.brand);
  return (
    <Card id={id} className={cn("relative overflow-hidden", className)}>
      <div className={`absolute top-0 left-0 right-0 ${ACCENT_BAR_CARD} bg-gradient-to-r ${barGradient}`} />
      {children}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Gradient Dialog — Radix Dialog with a tone-colored top accent bar
// ---------------------------------------------------------------------------

interface GradientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Tone drives the accent bar. Defaults to "info". Use "warning" for destructive confirms, "success" for completions, "danger" for final warnings. */
  tone?: Tone;
  /** Escape hatch: override the tone-derived gradient with a raw GRADIENTS string. */
  gradient?: string;
  /** Tailwind max-width class for the dialog. */
  maxWidth?: string;
  /**
   * When true (default), Radix's outside-click handler closes the
   * dialog. Set to false on long-form creation/edit dialogs where
   * a stray click on the backdrop would destroy unsaved input —
   * the consumer is then responsible for an explicit close path
   * (Cancel button, programmatic close after save, or the X
   * affordance). Pair with `onInteractOutside` to surface a
   * "Discard changes?" confirmation instead.
   */
  closeOnOutsideInteraction?: boolean;
  /**
   * When true (default), pressing Escape closes the dialog. Same
   * caveats as `closeOnOutsideInteraction`. Forms that call
   * `onEscapeKeyDown` and `preventDefault()` themselves should
   * leave this true and intercept in the handler instead.
   */
  closeOnEscape?: boolean;
  /**
   * Fired when the user attempts an outside click. Receives the
   * raw Radix event so the caller can `preventDefault()` to keep
   * the dialog open and run their own confirmation flow.
   */
  onInteractOutside?: (event: Event) => void;
  /**
   * Fired when the user presses Escape. Receives the raw Radix
   * event; `preventDefault()` keeps the dialog open.
   */
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  children: React.ReactNode;
}

export function GradientDialog({
  open, onOpenChange, tone = "brand", gradient, maxWidth = "sm:max-w-md",
  closeOnOutsideInteraction = true, closeOnEscape = true,
  onInteractOutside, onEscapeKeyDown, children,
}: GradientDialogProps) {
  const barGradient = gradient ?? TONES[tone].gradient;
  // Wrap caller handlers so we can guarantee the
  // closeOn{Outside,Escape}=false contract even when the caller
  // doesn't bother calling preventDefault themselves. Radix's
  // event objects expose preventDefault; when set, the dialog
  // does not transition to closed.
  const handleInteractOutside = (event: Event) => {
    onInteractOutside?.(event);
    if (!closeOnOutsideInteraction) {
      event.preventDefault();
    }
  };
  const handleEscapeKeyDown = (event: KeyboardEvent) => {
    onEscapeKeyDown?.(event);
    if (!closeOnEscape) {
      event.preventDefault();
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`${maxWidth} gap-0 p-0`}
        onInteractOutside={handleInteractOutside}
        onEscapeKeyDown={handleEscapeKeyDown}
      >
        <div
          className={`sticky top-0 z-10 ${ACCENT_BAR_DIALOG} bg-gradient-to-r ${barGradient}`}
          aria-hidden
        />
        <div className="flex flex-col gap-4 p-6">{children}</div>
      </DialogContent>
    </Dialog>
  );
}

// Dialog parts (DialogHeader, DialogTitle, etc.) are already exported
// from "@pureid/ui" via the dialog module — no re-export needed here.
